import Country from '../../../../../types/country/Country';
import PhysicalSample from '../../../../../types/physical-sample/PhysicalSample';
import PhysicalSampleFirmwareUpgradeDTO from '../../../../../types/physical-sample/PhysicalSampleFirmwareUpgradeDTO';
import StudioUpgradeTarget from '../../../../../types/sample-firmware-upgrade/StudioUpgradeTarget';
import Studio from '../../../../../types/studio/Studio';

/**
 * Normalizes a raw PCB/hardware-revision string for comparison - case-insensitive, trimmed.
 *
 * @param pcbValue The raw value to normalize.
 * @return The normalized value, or an empty string for a nullish input.
 */
export function normalizePcbVersion(pcbValue: string | null | undefined): string {
    return ( pcbValue ?? '' ).trim().toUpperCase();
}

/**
 * Explicit studio selection wins, else falls back to countries, else (Global scope) to all studios.
 *
 * @param dto The firmware upgrade scope selection.
 * @param allStudios Every known studio, used as the fallback pool.
 * @return The studios targeted by the selection.
 */
export function resolveTargetStudios(dto: PhysicalSampleFirmwareUpgradeDTO, allStudios: Studio[]): Studio[] {
    let targetStudios: Studio[] = dto.studios;
    if (!targetStudios.length && dto.countries.length) {
        targetStudios = allStudios.filter(studio =>
            dto.countries.some((country: Country) => country.name === studio.dealership?.country?.name)
        );
    }
    if (!targetStudios.length) {
        targetStudios = allStudios;
    }
    return targetStudios;
}

/**
 * Pilot selection is deliberately left to the Studio Hub, which has fresher online-status data.
 *
 * @param physicalSamples The manually chosen samples to upgrade.
 * @return One upgrade target per studio, each carrying its chosen samples' mac addresses.
 */
export function groupMacAddressesByStudio(physicalSamples: PhysicalSample[]): StudioUpgradeTarget[] {
    const map = new Map<number, { studioId: number; macAddresses: Set<string> }>();
    for (const p of physicalSamples) {
        if (!p.studio?.id) continue;
        if (!p.macAddress) continue;

        if (!map.has(p.studio.id)) {
            map.set(p.studio.id, { studioId: p.studio.id, macAddresses: new Set() });
        }
        map.get(p.studio.id)!.macAddresses.add(p.macAddress);
    }

    return Array.from(map.values()).map(({ studioId, macAddresses }) =>
        new StudioUpgradeTarget({ studioId, allEligibleSamples: false, macAddresses: Array.from(macAddresses) }));
}

/**
 * Unmodified scope selections send allEligibleSamples: true so the backend resolves samples itself;
 * manual selections send an explicit mac address list.
 *
 * @param targetStudios The studios targeted by the selection.
 * @param chosenPhysicalSamples The manually chosen samples (used only when allEligibleSamples is false).
 * @param allEligibleSamples Whether the selection is unmodified (every eligible sample) or manually narrowed.
 * @return One upgrade target per studio.
 */
export function buildStudioUpgradeTargets(
    targetStudios: Studio[],
    chosenPhysicalSamples: PhysicalSample[],
    allEligibleSamples: boolean
): StudioUpgradeTarget[] {
    if (!allEligibleSamples) {
        return groupMacAddressesByStudio(chosenPhysicalSamples);
    }
    return targetStudios
        .filter(studio => studio.id != null)
        .map(studio => new StudioUpgradeTarget({ studioId: studio.id, allEligibleSamples: true, macAddresses: [] }));
}
